import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Form, Alert } from 'react-bootstrap';
import { Formik, Field, FieldArray } from 'formik';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';

import { DEFAULT_PROVIDER } from '../../constants';

import { appsApi, adminApi } from '../../api';

import { useAlerts } from '../context';

import FieldWrapper from '../form/wrappers/Field';
import Loader from '../common/Loader';
import LoaderButton from '../common/LoaderButton';
import CopyButton from '../common/CopyButton';

const DEFAULT_VALUES = {
  name: 'Sila-DebitCard-Refund-Workflow',
  provider: DEFAULT_PROVIDER,
};

function CkoWebhooksForm({ appId, admin = false }) {
  const [initialValues, setInitialValues] = useState({
    webhooks: [DEFAULT_VALUES],
  });
  const [currentWebhook, setCurrentWebhook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(false);
  const [generated, setGenerated] = useState(false);
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const formRef = useRef(null);
  const authKeyRef = useRef(null);
  const signKeyRef = useRef(null);

  const getProviderWebhooks = async () => {
    setIsLoading(true);
    try {
      const [credentialsResponse, webhooksResponse] = await Promise.all(
        admin
          ? [adminApi.getCkoCredentials(appId), adminApi.getProviderWebhooks(appId)]
          : [appsApi.getCkoCredentials(appId), appsApi.getProviderWebhooks(appId)]
      );
      if (!credentialsResponse.success) {
        setError(credentialsResponse.message);
        return;
      }
      if (!webhooksResponse.success) {
        setError(webhooksResponse.message);
        return;
      }
      setInitialValues({
        webhooks: webhooksResponse.webhooks.length
          ? webhooksResponse.webhooks.filter((webhook) => webhook.provider === DEFAULT_PROVIDER)
          : [DEFAULT_VALUES],
      });
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProviderWebhooks = async (values) => {
    if (values.id) {
      setIsDeleting(true);
      try {
        const response = await adminApi.deleteProviderWebhooks(appId, values);
        setAlert({ message: response.message, variant: 'success' });
        getProviderWebhooks();
      } catch (err) {
        setError(err);
      } finally {
        setIsDeleting(false);
        setGenerated(false);
      }
    }
  };

  useEffect(() => {
    getProviderWebhooks();
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      innerRef={formRef}
      validationSchema={yup.object().shape({
        webhooks: yup.array().of(
          yup.object().shape({
            name: yup.string().required(
              t('common:form.errors.required', {
                field: t('applications.providerWebhooks.form.fields.name.label'),
              })
            ),
          })
        ),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        const index = values.webhooks.findIndex((webhook) => webhook.id === currentWebhook.id);
        const method = !initialValues.webhooks[index].id ? 'post' : 'put';
        let response;
        if (initialValues.webhooks[index].id) {
          setGenerated(true);
          // eslint-disable-next-line no-param-reassign
          values.webhooks[index].authentication_key = uuid();
          // eslint-disable-next-line no-param-reassign
          values.webhooks[index].signing_key = uuid();
        }
        try {
          if (admin) {
            response = await adminApi.updateProviderWebhooks(appId, values.webhooks[index], method);
          } else {
            // eslint-disable-next-line no-unused-vars
            response = await appsApi.updateProviderWebhooks(appId, values.webhooks[index], method);
          }
          setAlert({
            message: t('applications.providerWebhooks.form.messages.success'),
            variant: 'success',
          });
          if (error) setError(false);
          getProviderWebhooks();
        } catch (err) {
          setAlert({ message: err, variant: 'warning' });
          setInitialValues({ values: initialValues });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, handleSubmit, isSubmitting }) => (
        <Form noValidate className="loaded" autoComplete="new-password" onSubmit={handleSubmit}>
          {error && (
            <Alert variant="warning" className="mb-4 loaded">
              <i className="fas fa-exclamation-triangle text-warning me-2" /> {error}
            </Alert>
          )}

          {(isSubmitting || isLoading) && <Loader />}

          <h4 className="text-hdr mb-4">{t('applications.providerWebhooks.headers.refund')}</h4>

          <FieldArray
            name="webhooks"
            render={() => (
              <>
                {values.webhooks.map((webhook, index) => (
                  <Fragment key={webhook.id || index}>
                    <Field
                      required
                      readOnly
                      id={`appCkoWebhooksForm.${appId}.webhook.${index}.name`}
                      name={`webhooks.${index}.name`}
                      label={t('applications.providerWebhooks.form.fields.name.label')}
                      component={FieldWrapper}
                    />

                    <Field
                      required
                      readOnly
                      id={`appCkoWebhooksForm.${appId}.webhook.${index}.authentication_key`}
                      name={`webhooks.${index}.authentication_key`}
                      label={t('applications.providerWebhooks.form.fields.authKey.label')}
                      placeholder={
                        !isLoading
                          ? t('applications.providerWebhooks.form.fields.authKey.placeholder')
                          : t('common:status.loading')
                      }
                      inputRef={authKeyRef}
                      append={
                        <div className="floating-append">
                          {generated && (
                            <strong className="text-success me-2 loaded">
                              {t('applications.labels.generated')}
                            </strong>
                          )}
                          {webhook.id && document.queryCommandSupported('copy') && (
                            <CopyButton
                              inputRef={authKeyRef}
                              variant="link"
                              placement="top"
                              className="p-1"
                            />
                          )}
                        </div>
                      }
                      component={FieldWrapper}
                      className="pt-4"
                    />

                    <Field
                      className="pt-4"
                      required
                      readOnly
                      id={`appCkoWebhooksForm.${appId}.webhook.${index}.signing_key`}
                      name={`webhooks.${index}.signing_key`}
                      label={t('applications.providerWebhooks.form.fields.signKey.label')}
                      placeholder={
                        !isLoading
                          ? t('applications.providerWebhooks.form.fields.signKey.placeholder')
                          : t('common:status.loading')
                      }
                      inputRef={signKeyRef}
                      append={
                        <div className="floating-append">
                          {generated && (
                            <strong className="text-success me-2 loaded">
                              {t('applications.labels.generated')}
                            </strong>
                          )}
                          {webhook.id && document.queryCommandSupported('copy') && (
                            <CopyButton
                              inputRef={signKeyRef}
                              variant="link"
                              placement="top"
                              className="p-1"
                            />
                          )}
                        </div>
                      }
                      component={FieldWrapper}
                    />

                    <div
                      className={`actions d-flex flex-wrap justify-content-end ${
                        !admin ? 'mx-0 mb-0 mt-4 p-0 mx-md-n4 mb-md-n4 p-md-4' : 'mt-4'
                      }`}
                    >
                      <div className="me-0 me-md-auto">
                        {admin && webhook.id && (
                          <LoaderButton
                            variant="outline-warning"
                            disabled={isDeleting}
                            loading={isDeleting}
                            loadingLabel={t('common:status.deleting')}
                            onClick={() => deleteProviderWebhooks(webhook)}
                            className="loaded"
                          >
                            {t('common:buttons.delete')}
                          </LoaderButton>
                        )}
                      </div>

                      <LoaderButton
                        variant="primary"
                        disabled={error}
                        loading={isLoading || isSubmitting ? true : undefined}
                        loadingLabel={isLoading ? t('common:status.loading') : undefined}
                        type="submit"
                        onClick={() => setCurrentWebhook(webhook)}
                        className={!admin ? 'ms-0 ms-md-3 px-4' : undefined}
                      >
                        {webhook.id
                          ? t('applications.providerWebhooks.form.buttons.regenerate')
                          : t('applications.providerWebhooks.form.buttons.create')}
                      </LoaderButton>
                    </div>
                  </Fragment>
                ))}
              </>
            )}
          />
        </Form>
      )}
    </Formik>
  );
}

CkoWebhooksForm.propTypes = {
  /**
   * The id of the application
   */
  appId: PropTypes.number.isRequired,
  /**
   * The admin toggle to determine if the form is used for the admin console
   */
  admin: PropTypes.bool,
};

export default CkoWebhooksForm;
